import * as fs from "fs/promises";
import * as path from "path";

import simpleGit from "simple-git";

import { DEFAULT_CONFIG, ENV_CONSTANTS, GIT_CONSTANTS, PATH_CONSTANTS } from "../constants";
import { GitOperationError, WorktreeError, WorktreeNotCleanError } from "../errors";
import { probePathExists } from "../utils/file-exists";
import { makeGitProgressHandler } from "../utils/git-progress";
import { getDefaultBareRepoDir } from "../utils/git-url";
import { getErrorMessage } from "../utils/lfs-error";
import { quarantineDirectory } from "../utils/quarantine";
import { parseWorktreeListPorcelain } from "../utils/worktree-list-parser";

import { Logger } from "./logger.service";
import { SparseCheckoutService } from "./sparse-checkout.service";
import { WorktreeMetadataService } from "./worktree-metadata.service";
import { WorktreeStatusService } from "./worktree-status.service";

import type { WorktreeStatusResult } from "./worktree-status.service";
import type { Config } from "../types";
import type { SyncMetadata } from "../types/sync-metadata";
import type { GitProgressEmitter } from "../utils/git-progress";
import type { SimpleGit, SimpleGitOptions } from "simple-git";

export type RemoteRelationship = "up_to_date" | "fast_forward" | "local_ahead" | "diverged" | "indeterminate_shallow";

export type GitServiceOptions = Pick<
  Config,
  | "repoUrl"
  | "worktreeDir"
  | "bareRepoDir"
  | "skipLfs"
  | "debug"
  | "sparseCheckout"
  | "fetchTimeoutMs"
  | "cloneTimeoutMs"
>;

// simple-git blocks EDITOR / GIT_EDITOR / GIT_SEQUENCE_EDITOR unless allowUnsafeEditor is set;
// strip them when forwarding process.env so a user's shell EDITOR doesn't break read-only commands.
function sanitizeGitEnv(env: NodeJS.ProcessEnv): NodeJS.ProcessEnv {
  const sanitized = { ...env };
  delete sanitized.EDITOR;
  delete sanitized.GIT_EDITOR;
  delete sanitized.GIT_SEQUENCE_EDITOR;
  return sanitized;
}

export class GitService {
  private git: SimpleGit | null = null;
  private bareRepoPath: string;
  private mainWorktreePath: string;
  private defaultBranch: string = GIT_CONSTANTS.DEFAULT_BRANCH; // Will be updated after detection
  private metadataService: WorktreeMetadataService;
  private statusService: WorktreeStatusService;
  private sparseCheckoutService: SparseCheckoutService;
  private logger: Logger;
  private lfsSkipOverride = false;
  private gitInstances = new Map<string, SimpleGit>();

  constructor(
    private config: GitServiceOptions,
    logger?: Logger,
    private progressEmitter?: GitProgressEmitter,
  ) {
    this.logger = logger ?? Logger.createDefault(undefined, config.debug);
    this.bareRepoPath = this.config.bareRepoDir || getDefaultBareRepoDir(this.config.repoUrl);
    this.mainWorktreePath = path.join(this.config.worktreeDir, GIT_CONSTANTS.DEFAULT_BRANCH); // Temporary, will be updated
    this.metadataService = new WorktreeMetadataService(this.logger);
    this.statusService = new WorktreeStatusService({ skipLfs: this.config.skipLfs }, this.logger);
    this.sparseCheckoutService = new SparseCheckoutService(this.logger);
  }

  getSparseCheckoutService(): SparseCheckoutService {
    return this.sparseCheckoutService;
  }

  private getFetchTimeoutMs(): number {
    if (process.env.NODE_ENV === ENV_CONSTANTS.NODE_ENV_TEST) return 0;
    return this.config.fetchTimeoutMs ?? DEFAULT_CONFIG.FETCH_TIMEOUT_MS;
  }

  private getCloneTimeoutMs(): number {
    if (process.env.NODE_ENV === ENV_CONSTANTS.NODE_ENV_TEST) return 0;
    return this.config.cloneTimeoutMs ?? DEFAULT_CONFIG.CLONE_TIMEOUT_MS;
  }

  private getCachedGit(dirPath: string, useLfsSkip = false): SimpleGit {
    const key = `${path.resolve(dirPath)}::${useLfsSkip ? "1" : "0"}`;
    let git = this.gitInstances.get(key);
    if (!git) {
      const base = simpleGit(dirPath, this.buildSimpleGitOptions(this.getFetchTimeoutMs()));
      git = useLfsSkip ? base.env({ [ENV_CONSTANTS.GIT_LFS_SKIP_SMUDGE]: "1" }) : base;
      this.gitInstances.set(key, git);
    }
    return git;
  }

  private buildSimpleGitOptions(blockMs: number): Partial<SimpleGitOptions> {
    const options: Partial<SimpleGitOptions> = {
      progress: makeGitProgressHandler(this.logger, (event) => this.progressEmitter?.(event)),
    };
    if (blockMs > 0) options.timeout = { block: blockMs };
    return options;
  }

  updateLogger(logger: Logger): void {
    this.logger = logger;
    this.sparseCheckoutService.updateLogger(logger);
  }

  async initialize(): Promise<SimpleGit> {
    const { repoUrl } = this.config;

    try {
      // Check if bare repo already exists
      await fs.access(path.join(this.bareRepoPath, "HEAD"));
    } catch {
      // Clone as bare repository
      this.logger.info(`Cloning from "${repoUrl}" as bare repository into "${this.bareRepoPath}"...`);
      await fs.mkdir(path.dirname(this.bareRepoPath), { recursive: true });
      const cloneBase = simpleGit(this.buildSimpleGitOptions(this.getCloneTimeoutMs()));
      const cloneGit = this.isLfsSkipEnabled()
        ? cloneBase.env({ [ENV_CONSTANTS.GIT_LFS_SKIP_SMUDGE]: "1" })
        : cloneBase;
      await cloneGit.clone(repoUrl, this.bareRepoPath, ["--bare", "--progress"]);
      this.logger.info("✅ Clone successful.");
    }

    // Configure bare repository for worktrees
    const bareGit = this.getCachedGit(this.bareRepoPath);

    // Check if fetch config already exists
    try {
      const existingConfig = await bareGit.raw(["config", "--get-all", "remote.origin.fetch"]);
      const targetConfig = "+refs/heads/*:refs/remotes/origin/*";

      if (!existingConfig.includes(targetConfig)) {
        await bareGit.addConfig("remote.origin.fetch", targetConfig);
      }
    } catch {
      // Config doesn't exist, add it
      await bareGit.addConfig("remote.origin.fetch", "+refs/heads/*:refs/remotes/origin/*");
    }

    // Always fetch to ensure remote refs are up-to-date
    // This is needed for branch creation UI even when repo already exists
    this.logger.info("Fetching remote branches...");
    await bareGit.fetch(["--all", "--progress"]);

    // Detect the default branch (works from local refs even without fetch)
    this.defaultBranch = await this.detectDefaultBranch(bareGit);
    this.mainWorktreePath = path.join(this.config.worktreeDir, this.defaultBranch);

    // Check if main worktree exists
    let needsMainWorktree = true;
    try {
      const worktrees = await this.getWorktreesFromBare(bareGit, true);
      needsMainWorktree = !worktrees.some((w) => path.resolve(w.path) === path.resolve(this.mainWorktreePath));
    } catch {
      // If worktree list fails, assume we need main worktree
    }

    if (needsMainWorktree) {
      // Create main worktree if it doesn't exist
      this.logger.info(`Creating ${this.defaultBranch} worktree at "${this.mainWorktreePath}"...`);
      await fs.mkdir(this.config.worktreeDir, { recursive: true });
      // Use absolute path for worktree add to avoid relative path issues
      const absoluteWorktreePath = path.resolve(this.mainWorktreePath);

      // Check if local branch exists
      const branches = await bareGit.branch();
      const defaultBranchExists = branches.all.includes(this.defaultBranch);

      const useNoCheckoutMain = !!this.config.sparseCheckout;
      const noCheckoutFlagMain = useNoCheckoutMain ? ["--no-checkout"] : [];

      try {
        if (defaultBranchExists) {
          await bareGit.raw(["worktree", "add", ...noCheckoutFlagMain, absoluteWorktreePath, this.defaultBranch]);
          const worktreeGit = this.getCachedGit(absoluteWorktreePath, this.isLfsSkipEnabled());
          await worktreeGit.branch(["--set-upstream-to", `origin/${this.defaultBranch}`, this.defaultBranch]);
          await this.runSparseStepWithRollback(bareGit, absoluteWorktreePath, this.defaultBranch, false);
        } else {
          await bareGit.raw([
            "worktree",
            "add",
            ...noCheckoutFlagMain,
            "--track",
            "-b",
            this.defaultBranch,
            absoluteWorktreePath,
            `origin/${this.defaultBranch}`,
          ]);
          await this.runSparseStepWithRollback(bareGit, absoluteWorktreePath, this.defaultBranch, true);
        }
      } catch (error) {
        const errorMessage = getErrorMessage(error);
        if (errorMessage.includes("already exists")) {
          this.logger.info(
            `${this.defaultBranch} worktree directory already exists at '${absoluteWorktreePath}', skipping creation.`,
          );
        } else {
          throw error;
        }
      }

      // Ensure the worktree is registered by checking it exists in the list
      const updatedWorktrees = await this.getWorktreesFromBare(bareGit, true);
      const mainWorktreeRegistered = updatedWorktrees.some(
        (w) => path.resolve(w.path) === path.resolve(this.mainWorktreePath),
      );

      if (!mainWorktreeRegistered) {
        // Only warn in non-test environments as this is common in tests due to Git state
        if (process.env.NODE_ENV !== ENV_CONSTANTS.NODE_ENV_TEST) {
          this.logger.warn(`Main worktree was created but not found in worktree list. This may cause issues.`);
        }
      }
    }

    // Use the main worktree as our primary git instance
    this.git = this.getCachedGit(this.mainWorktreePath);
    return this.git;
  }

  getGit(): SimpleGit {
    if (!this.git) {
      throw new Error("Git service not initialized. Call initialize() first.");
    }
    return this.git;
  }

  isInitialized(): boolean {
    return this.git !== null;
  }

  getDefaultBranch(): string {
    return this.defaultBranch;
  }

  getBareRepoPath(): string {
    return this.bareRepoPath;
  }

  async getRemoteDefaultBranch(repoUrl: string): Promise<string> {
    const git = simpleGit(this.buildSimpleGitOptions(this.getFetchTimeoutMs()));

    try {
      const out = await git.raw(["ls-remote", "--symref", repoUrl, "HEAD"]);
      const match = out.match(/^ref: refs\/heads\/(\S+)\s+HEAD/m);
      if (match && match[1]) {
        return match[1];
      }
    } catch {
      /* fall through to probe candidates */
    }

    // symref HEAD was unavailable/unparsed: probe common branch names, but only
    // auto-pick when the choice is unambiguous. Guessing by fixed priority when
    // several exist can silently track the wrong branch (e.g. 'main' when the
    // remote's real default is 'master').
    const existing: string[] = [];
    for (const candidate of GIT_CONSTANTS.COMMON_DEFAULT_BRANCHES) {
      try {
        const out = await git.raw(["ls-remote", "--exit-code", repoUrl, `refs/heads/${candidate}`]);
        if (out.trim().length > 0) {
          existing.push(candidate);
        }
      } catch {
        /* candidate missing — try next */
      }
    }

    if (existing.length === 1) {
      this.logger.warn(
        `Could not read symref HEAD for '${repoUrl}'; using the only common branch found ('${existing[0]}') as the default.`,
      );
      return existing[0];
    }

    if (existing.length > 1) {
      throw new Error(
        `Unable to detect default branch for '${repoUrl}': symref HEAD is unavailable and multiple common branches exist (${existing.join(", ")}). ` +
          `Set 'branch' explicitly in the repository config.`,
      );
    }

    throw new Error(
      `Unable to detect default branch for '${repoUrl}'. ` +
        `Set 'branch' explicitly in the repository config or ensure the remote is reachable.`,
    );
  }

  async verifyLfs(worktreePath: string, label: string): Promise<void> {
    if (this.isLfsSkipEnabled()) return;
    await this.verifyLfsFilesDownloaded(worktreePath, label);
  }

  async fetchAll(): Promise<void> {
    this.assertInitialized();
    this.logger.info("Fetching latest data from remote...");
    const git = this.getCachedGit(this.mainWorktreePath, this.isLfsSkipEnabled());
    await git.fetch(["--all", "--prune", "--progress"]);
  }

  async fetchBranch(branchName: string): Promise<void> {
    this.assertInitialized();
    const git = this.getCachedGit(this.mainWorktreePath, this.isLfsSkipEnabled());
    await git.fetch(["origin", branchName, "--prune", "--progress"]);
  }

  private assertInitialized(): void {
    if (!this.git) {
      throw new Error("Git service not initialized. Call initialize() first.");
    }
  }

  async getRemoteBranches(): Promise<string[]> {
    const git = this.getGit();
    const branches = await git.branch(["-r"]);
    return branches.all
      .filter((b) => b.startsWith("origin/") && !b.endsWith("/HEAD"))
      .map((b) => b.replace("origin/", ""))
      .filter((b) => b !== "origin" && b.length > 0);
  }

  async getRemoteBranchesWithActivity(): Promise<{ branch: string; lastActivity: Date }[]> {
    const git = this.getGit();
    // Use for-each-ref to get branch names with their last commit dates
    const result = await git.raw([
      "for-each-ref",
      "--format=%(refname:short)|%(committerdate:iso8601)",
      "refs/remotes/origin",
    ]);

    const branches: { branch: string; lastActivity: Date }[] = [];
    const lines = result
      .trim()
      .split("\n")
      .filter((line) => line);

    for (const line of lines) {
      const [ref, dateStr] = line.split("|", 2);
      if (ref && dateStr && !ref.endsWith("/HEAD")) {
        const branch = ref.replace("origin/", "");
        // Skip invalid branch names
        if (branch === "origin" || branch.length === 0) {
          continue;
        }
        const lastActivity = new Date(dateStr);
        // Skip if the date is invalid
        if (!isNaN(lastActivity.getTime())) {
          branches.push({ branch, lastActivity });
        }
      }
    }

    return branches;
  }

  private async verifyLfsFilesDownloaded(worktreePath: string, branchName: string): Promise<void> {
    const worktreeGit = this.config.sparseCheckout
      ? simpleGit(worktreePath).env({ ...sanitizeGitEnv(process.env), [ENV_CONSTANTS.GIT_ATTR_SOURCE]: "HEAD" })
      : this.getCachedGit(worktreePath);

    try {
      const lfsFiles = await worktreeGit.raw(["lfs", "ls-files", "--name-only"]);
      let lfsFileList = lfsFiles
        .trim()
        .split("\n")
        .filter((f) => f.length > 0);

      if (lfsFileList.length === 0) {
        return;
      }

      // GIT_ATTR_SOURCE=HEAD lists every LFS file declared in HEAD's .gitattributes,
      // including ones outside the sparse-checkout cone that aren't on disk. Sampling
      // those would burn the 30s retry loop on guaranteed-missing files.
      if (this.config.sparseCheckout) {
        const existence = await Promise.all(
          lfsFileList.map(async (f) => {
            try {
              await fs.access(path.join(worktreePath, f));
              return f;
            } catch {
              return null;
            }
          }),
        );
        lfsFileList = existence.filter((f): f is string => f !== null);
        if (lfsFileList.length === 0) {
          return;
        }
      }

      if (this.config.debug) {
        this.logger.info(`  - Verifying ${lfsFileList.length} LFS files are downloaded...`);
      }

      const sampleSize = Math.min(5, lfsFileList.length);
      const shuffled = [...lfsFileList];
      for (let i = 0; i < sampleSize; i++) {
        const randomIndex = i + Math.floor(Math.random() * (shuffled.length - i));
        [shuffled[i], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[i]];
      }
      const samplesToCheck = shuffled.slice(0, sampleSize);

      let retries = 0;
      const maxRetries = DEFAULT_CONFIG.LFS_VERIFICATION_MAX_RETRIES;
      const retryDelay = 1000;

      while (retries < maxRetries) {
        let allDownloaded = true;
        const notDownloaded: string[] = [];

        for (const file of samplesToCheck) {
          const filePath = path.join(worktreePath, file);
          try {
            const handle = await fs.open(filePath, "r");
            try {
              const buffer = Buffer.alloc(200);
              const { bytesRead } = await handle.read(buffer, 0, buffer.length, 0);
              const header = buffer.subarray(0, bytesRead).toString("utf8");
              if (header.startsWith(GIT_CONSTANTS.LFS_HEADER)) {
                allDownloaded = false;
                notDownloaded.push(file);
              }
            } finally {
              await handle.close();
            }
          } catch {
            allDownloaded = false;
            notDownloaded.push(file);
          }
        }

        if (allDownloaded) {
          if (this.config.debug) {
            this.logger.info(`  - ✅ LFS files verified (${samplesToCheck.length} samples checked)`);
          }
          return;
        }

        retries++;
        if (retries < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
        }
      }

      this.logger.warn(
        `  - ⚠️ Warning: Some LFS files may not be fully downloaded after ${maxRetries} seconds. ` +
          `This might cause issues if tools access the worktree immediately.`,
      );
    } catch (error) {
      this.logger.warn(`  - ⚠️ Warning: Could not verify LFS files for '${branchName}': ${error}`);
    }
  }

  async checkoutHead(worktreePath: string): Promise<void> {
    const git = this.getCachedGit(worktreePath, this.isLfsSkipEnabled());
    await git.raw(["checkout", "HEAD"]);
  }

  private async applySparseAndCheckout(absoluteWorktreePath: string): Promise<void> {
    if (!this.config.sparseCheckout) return;
    await this.sparseCheckoutService.applyToWorktree(absoluteWorktreePath, this.config.sparseCheckout);
    const worktreeGit = this.getCachedGit(absoluteWorktreePath, this.isLfsSkipEnabled());
    await worktreeGit.raw(["checkout", "HEAD"]);
  }

  private async rollbackPartialWorktree(
    bareGit: SimpleGit,
    absoluteWorktreePath: string,
    branchName: string,
    createdNewBranch: boolean,
    failureContext?: string,
  ): Promise<{ worktreeRemoved: boolean }> {
    let worktreeRemoved = true;
    try {
      await bareGit.raw(["worktree", "remove", "--force", absoluteWorktreePath]);
    } catch (rollbackError) {
      worktreeRemoved = false;
      const ctx = failureContext ? ` after ${failureContext}` : "";
      this.logger.warn(
        `  - Rollback failed for '${branchName}' at '${absoluteWorktreePath}'${ctx}: ${getErrorMessage(rollbackError)}`,
      );
    }
    if (createdNewBranch) {
      try {
        await bareGit.raw(["branch", "-D", branchName]);
      } catch (branchRollbackError) {
        this.logger.warn(
          `  - Rollback (branch delete) failed for '${branchName}': ${getErrorMessage(branchRollbackError)}`,
        );
      }
    }
    return { worktreeRemoved };
  }

  private async createWorktreeMetadata(bareGit: SimpleGit, worktreePath: string, branchName: string): Promise<void> {
    try {
      const worktreeGit = this.getCachedGit(worktreePath, this.isLfsSkipEnabled());
      const currentCommit = await worktreeGit.revparse(["HEAD"]);
      const parentCommit = await bareGit.revparse([this.defaultBranch]);

      await this.metadataService.createInitialMetadataFromPath(
        this.bareRepoPath,
        worktreePath,
        currentCommit.trim(),
        `origin/${branchName}`,
        this.defaultBranch,
        parentCommit.trim(),
      );
    } catch (metadataError) {
      this.logger.error(`  - ❌ Failed to create metadata for '${branchName}': ${metadataError}`);
      throw new Error(`Metadata creation failed for ${branchName}. This worktree cannot be auto-managed.`);
    }
  }

  async addWorktree(branchName: string, worktreePath: string): Promise<void> {
    const bareGit = this.getCachedGit(this.bareRepoPath, this.isLfsSkipEnabled());
    // Use absolute path for worktree add to avoid relative path issues
    const absoluteWorktreePath = path.resolve(worktreePath);
    // Ensure parent directory exists for nested branch paths
    await fs.mkdir(path.dirname(absoluteWorktreePath), { recursive: true });

    // Check if directory already exists (could be from a failed previous attempt)
    try {
      await fs.access(absoluteWorktreePath);
      // Directory exists - check if it's already a valid worktree
      const worktrees = await this.getWorktreesFromBare(bareGit, true);
      const isValidWorktree = worktrees.some((w) => path.resolve(w.path) === absoluteWorktreePath);

      if (isValidWorktree) {
        this.logger.info(`  - Worktree for '${branchName}' already exists at '${absoluteWorktreePath}'`);
        return;
      } else {
        // Directory exists but is not a valid worktree - clean it up
        this.logger.info(`  - Cleaning up orphaned directory at '${absoluteWorktreePath}'`);
        await this.clearStaleWorktreeDirectory(absoluteWorktreePath);
      }
    } catch (error) {
      if (error instanceof GitOperationError || error instanceof WorktreeError) {
        throw error;
      }
      // Directory doesn't exist, which is expected - continue with creation
    }

    let createdNewBranch = false;
    try {
      const { local: localBranchExists, remote: remoteBranchExists } = await this.branchExists(branchName);

      createdNewBranch = await this.runWorktreeAddByMatrix(
        bareGit,
        branchName,
        absoluteWorktreePath,
        localBranchExists,
        remoteBranchExists,
      );

      if (localBranchExists && !remoteBranchExists) {
        this.logger.info(`  - Created worktree for '${branchName}' (no remote yet — push to set upstream)`);
      } else {
        this.logger.info(`  - Created worktree for '${branchName}' with tracking to origin/${branchName}`);
      }

      if (!this.isLfsSkipEnabled()) {
        await this.verifyLfsFilesDownloaded(absoluteWorktreePath, branchName);
      }

      try {
        await this.createWorktreeMetadata(bareGit, absoluteWorktreePath, branchName);
      } catch (metadataError) {
        this.logger.warn(`  - Metadata creation failed for '${branchName}', removing worktree to prevent orphan`);
        await this.rollbackPartialWorktree(bareGit, absoluteWorktreePath, branchName, createdNewBranch);
        throw new Error(`Metadata creation failed for '${branchName}': ${getErrorMessage(metadataError)}`);
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);

      // Upstream setup failures are already rolled back inside runWorktreeAddByMatrix.
      // Don't enter the tracking-error fallback (which would silently accept a partial worktree).
      if ((error as { isUpstreamSetupFailure?: boolean })?.isUpstreamSetupFailure) {
        throw error;
      }

      // Re-throw metadata creation errors - these are fatal and should not fall back
      if (errorMessage.includes("Metadata creation failed")) {
        throw error;
      }

      // Check if this is an "already registered" error
      if (errorMessage.includes("already registered worktree")) {
        // Check if worktree was actually created by a concurrent operation
        const worktrees = await this.getWorktreesFromBare(bareGit, true);
        const existingWorktree = worktrees.find((w) => path.resolve(w.path) === absoluteWorktreePath);

        if (existingWorktree && !existingWorktree.isPrunable) {
          this.logger.info(`  - Worktree for '${branchName}' was created by concurrent operation`);
          return;
        }

        this.logger.warn(`  - Worktree already registered but missing. Removing that registration and retrying...`);
        try {
          await bareGit.raw(["worktree", "remove", "--force", absoluteWorktreePath]);
        } catch (removalError) {
          this.logger.warn(
            `  - Failed to remove stale registration for '${absoluteWorktreePath}': ${getErrorMessage(removalError)}. Continuing with directory cleanup and retry.`,
          );
        }
        await this.clearStaleWorktreeDirectory(absoluteWorktreePath);
        let retryCreatedNewBranch = false;
        try {
          const { local: localBranchExists, remote: remoteBranchExists } = await this.branchExists(branchName);
          retryCreatedNewBranch = await this.runWorktreeAddByMatrix(
            bareGit,
            branchName,
            absoluteWorktreePath,
            localBranchExists,
            remoteBranchExists,
          );
          this.logger.info(`  - Created worktree for '${branchName}' on retry`);

          if (!this.isLfsSkipEnabled()) {
            await this.verifyLfsFilesDownloaded(absoluteWorktreePath, branchName);
          }

          try {
            await this.createWorktreeMetadata(bareGit, absoluteWorktreePath, branchName);
          } catch (metadataError) {
            this.logger.warn(`  - Metadata creation failed for '${branchName}', removing worktree to prevent orphan`);
            await this.rollbackPartialWorktree(bareGit, absoluteWorktreePath, branchName, retryCreatedNewBranch);
            throw new Error(`Metadata creation failed for '${branchName}': ${getErrorMessage(metadataError)}`);
          }
          return;
        } catch (retryError) {
          this.logger.error(`  - Failed to create worktree on retry: ${retryError}`);
          throw retryError;
        }
      }

      // Only fall back to non-tracking version for tracking-related errors.
      // Re-throw real errors (disk full, permissions, etc.) immediately.
      const isTrackingError =
        errorMessage.includes("not a valid object name") ||
        errorMessage.includes("not a commit") ||
        errorMessage.includes("cannot set up tracking") ||
        errorMessage.includes("does not track") ||
        errorMessage.includes("remote tracking branch") ||
        errorMessage.includes("no such remote ref");

      if (!isTrackingError) {
        throw error;
      }

      this.logger.warn(`  - Failed to create worktree with tracking, falling back to simple add: ${error}`);

      // Check again if directory exists before fallback attempt
      try {
        await fs.access(absoluteWorktreePath);
        // Directory exists - check if it's already a valid worktree
        const worktrees = await this.getWorktreesFromBare(bareGit, true);
        const isValidWorktree = worktrees.some((w) => path.resolve(w.path) === absoluteWorktreePath);

        if (isValidWorktree) {
          this.logger.info(`  - Worktree for '${branchName}' already exists at '${absoluteWorktreePath}'`);
          return;
        } else {
          // Directory exists but is not a valid worktree - clean it up
          this.logger.info(`  - Cleaning up orphaned directory at '${absoluteWorktreePath}' before fallback attempt`);
          await this.clearStaleWorktreeDirectory(absoluteWorktreePath);
        }
      } catch (error) {
        if (error instanceof GitOperationError || error instanceof WorktreeError) {
          throw error;
        }
        // Directory doesn't exist, which is expected - continue with fallback
      }

      try {
        const useNoCheckout = !!this.config.sparseCheckout;
        const fallbackArgs = useNoCheckout
          ? ["worktree", "add", "--no-checkout", absoluteWorktreePath, branchName]
          : ["worktree", "add", absoluteWorktreePath, branchName];
        await bareGit.raw(fallbackArgs);
        await this.runSparseStepWithRollback(bareGit, absoluteWorktreePath, branchName, false);
        this.logger.info(`  - Created worktree for '${branchName}' (without tracking)`);

        if (!this.isLfsSkipEnabled()) {
          await this.verifyLfsFilesDownloaded(absoluteWorktreePath, branchName);
        }

        try {
          await this.createWorktreeMetadata(bareGit, absoluteWorktreePath, branchName);
        } catch (metadataError) {
          this.logger.warn(`  - Metadata creation failed for '${branchName}', removing worktree to prevent orphan`);
          await this.rollbackPartialWorktree(bareGit, absoluteWorktreePath, branchName, false);
          throw new Error(`Metadata creation failed for '${branchName}': ${getErrorMessage(metadataError)}`);
        }
      } catch (fallbackError) {
        const fallbackErrorMessage = getErrorMessage(fallbackError);

        // If fallback also fails with "already registered", check if created by concurrent op
        if (fallbackErrorMessage.includes("already registered worktree")) {
          const worktrees = await this.getWorktreesFromBare(bareGit, true);
          const existingWorktree = worktrees.find((w) => path.resolve(w.path) === absoluteWorktreePath);

          if (existingWorktree && !existingWorktree.isPrunable) {
            this.logger.info(`  - Worktree for '${branchName}' was created by concurrent operation during fallback`);
            return;
          }
        }

        // If still failing, this is a real error
        throw fallbackError;
      }
    }
  }

  private async runWorktreeAddByMatrix(
    bareGit: SimpleGit,
    branchName: string,
    absoluteWorktreePath: string,
    localExists: boolean,
    remoteExists: boolean,
  ): Promise<boolean> {
    const useNoCheckout = !!this.config.sparseCheckout;
    const noCheckoutFlag = useNoCheckout ? ["--no-checkout"] : [];

    if (localExists && remoteExists) {
      await bareGit.raw(["worktree", "add", ...noCheckoutFlag, absoluteWorktreePath, branchName]);

      // branch --set-upstream-to is a config-only operation and works on a --no-checkout
      // worktree, so we run it before sparse setup and materialization.
      try {
        const worktreeGit = this.getCachedGit(absoluteWorktreePath, this.isLfsSkipEnabled());
        await worktreeGit.branch(["--set-upstream-to", `origin/${branchName}`, branchName]);
      } catch (error) {
        throw await this.wrapUpstreamFailure(bareGit, absoluteWorktreePath, branchName, false, error);
      }

      await this.runSparseStepWithRollback(bareGit, absoluteWorktreePath, branchName, false);
      return false;
    }

    if (localExists) {
      await bareGit.raw(["worktree", "add", ...noCheckoutFlag, absoluteWorktreePath, branchName]);
      await this.runSparseStepWithRollback(bareGit, absoluteWorktreePath, branchName, false);
      return false;
    }

    if (remoteExists) {
      await bareGit.raw([
        "worktree",
        "add",
        ...noCheckoutFlag,
        "--track",
        "-b",
        branchName,
        absoluteWorktreePath,
        `origin/${branchName}`,
      ]);
      await this.runSparseStepWithRollback(bareGit, absoluteWorktreePath, branchName, true);
      return true;
    }

    throw new WorktreeError(
      `Branch '${branchName}' does not exist locally or on origin; create it first`,
      "BRANCH_NOT_FOUND",
    );
  }

  private async runSparseStepWithRollback(
    bareGit: SimpleGit,
    absoluteWorktreePath: string,
    branchName: string,
    createdNewBranch: boolean,
  ): Promise<void> {
    try {
      await this.applySparseAndCheckout(absoluteWorktreePath);
    } catch (sparseError) {
      await this.rollbackPartialWorktree(bareGit, absoluteWorktreePath, branchName, createdNewBranch);
      throw new Error(`Sparse-checkout setup failed for '${branchName}': ${getErrorMessage(sparseError)}`);
    }
  }

  private async wrapUpstreamFailure(
    bareGit: SimpleGit,
    absoluteWorktreePath: string,
    branchName: string,
    createdNewBranch: boolean,
    error: unknown,
  ): Promise<Error> {
    const { worktreeRemoved } = await this.rollbackPartialWorktree(
      bareGit,
      absoluteWorktreePath,
      branchName,
      createdNewBranch,
      "upstream setup error",
    );
    const suffix = worktreeRemoved ? "" : " (rollback failed; partial worktree may remain)";
    const wrapped = new Error(`Failed to set upstream for '${branchName}': ${getErrorMessage(error)}${suffix}`);
    (wrapped as Error & { isUpstreamSetupFailure?: boolean }).isUpstreamSetupFailure = true;
    return wrapped;
  }

  async removeWorktree(worktreePath: string, options?: { force?: boolean }): Promise<void> {
    const bareGit = this.getCachedGit(this.bareRepoPath);

    // Non-forced by default: git's own refusal to delete a dirty worktree is
    // the last line of defense when our status checks were wrong. --force is
    // reserved for callers that already preserved the data (diverged flow) or
    // explicit user override.
    const args = ["worktree", "remove", worktreePath];
    if (options?.force) args.push("--force");

    try {
      await bareGit.raw(args);
    } catch (error) {
      const message = getErrorMessage(error);
      if (!options?.force && /contains modified or untracked files|use --force/i.test(message)) {
        throw new WorktreeNotCleanError(worktreePath, [`git refused removal: ${message}`]);
      }
      throw error;
    }
    this.logger.info(`  - ✅ Safely removed stale worktree at '${worktreePath}'.`);

    // Clean up metadata using the worktree path
    try {
      await this.metadataService.deleteMetadataFromPath(this.bareRepoPath, worktreePath);
    } catch (metadataError) {
      this.logger.warn(`Failed to delete metadata for worktree: ${metadataError}`);
    }
  }

  async pruneWorktrees(): Promise<void> {
    const bareGit = this.getCachedGit(this.bareRepoPath);
    await bareGit.raw(["worktree", "prune"]);
    this.logger.info("Pruned worktree metadata.");
  }

  async updateRef(refName: string, sha: string): Promise<void> {
    const bareGit = this.getCachedGit(this.bareRepoPath);
    await bareGit.raw(["update-ref", refName, sha]);
  }

  async deleteRef(refName: string): Promise<void> {
    const bareGit = this.getCachedGit(this.bareRepoPath);
    await bareGit.raw(["update-ref", "-d", refName]);
  }

  async listRefs(prefix: string): Promise<string[]> {
    const bareGit = this.getCachedGit(this.bareRepoPath);
    const raw = await bareGit.raw(["for-each-ref", "--format=%(refname)", prefix]);
    return raw
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
  }

  async localBranchExists(branchName: string): Promise<boolean> {
    const bareGit = this.getCachedGit(this.bareRepoPath);
    try {
      await bareGit.raw(["show-ref", "--verify", "--quiet", `${GIT_CONSTANTS.REFS.HEADS}${branchName}`]);
      return true;
    } catch {
      return false;
    }
  }

  async getLocalBranchCommit(branchName: string): Promise<string | null> {
    const bareGit = this.getCachedGit(this.bareRepoPath);
    try {
      return (await bareGit.raw(["rev-parse", `${GIT_CONSTANTS.REFS.HEADS}${branchName}^{commit}`])).trim();
    } catch {
      return null;
    }
  }

  async createBranchAt(branchName: string, sha: string): Promise<void> {
    const bareGit = this.getCachedGit(this.bareRepoPath);
    await bareGit.raw(["branch", branchName, sha]);
  }

  async deleteLocalBranch(branchName: string): Promise<void> {
    const bareGit = this.getCachedGit(this.bareRepoPath);
    await bareGit.raw(["branch", "-D", branchName]);
  }

  // Bundles only commits not reachable from any remote — for fully-pushed
  // refs that set is empty and `bundle create` would fail. Emptiness is
  // pre-checked with rev-list (locale-independent) instead of parsing git's
  // localized "empty bundle" stderr; after the pre-check, any bundle-create
  // error is a real failure the caller must treat as fail-closed.
  async createBundleFromRef(bundlePath: string, refName: string): Promise<boolean> {
    const bareGit = this.getCachedGit(this.bareRepoPath);
    const count = (await bareGit.raw(["rev-list", "--count", refName, "--not", "--remotes"])).trim();
    if (count === "0") {
      return false;
    }
    await bareGit.raw(["bundle", "create", bundlePath, refName, "--not", "--remotes"]);
    return true;
  }

  // Registers the worktree and writes its .git link without populating files —
  // restore overlays the preserved payload instead of a fresh checkout.
  async addWorktreeNoCheckout(branchName: string, worktreePath: string): Promise<void> {
    const bareGit = this.getCachedGit(this.bareRepoPath);
    const absoluteWorktreePath = path.resolve(worktreePath);
    await fs.mkdir(path.dirname(absoluteWorktreePath), { recursive: true });
    await bareGit.raw(["worktree", "add", "--no-checkout", absoluteWorktreePath, branchName]);
  }

  // Mixed reset: points the index at HEAD without touching working files, so
  // overlaid payload content shows up as ordinary uncommitted changes.
  async resetWorktreeIndex(worktreePath: string): Promise<void> {
    const worktreeGit = this.getCachedGit(worktreePath);
    await worktreeGit.raw(["reset"]);
  }

  // Injected by WorktreeSyncService when trash is enabled, so stale-directory
  // cleanup follows the same reversible-removal pipeline as everything else.
  // GitService cannot own a TrashService directly (TrashService depends on it).
  private staleDirectoryTrasher: ((dirPath: string) => Promise<string>) | null = null;

  setStaleDirectoryTrasher(trasher: (dirPath: string) => Promise<string>): void {
    this.staleDirectoryTrasher = trasher;
  }

  // A stale directory that contains a .git may be a live checkout that git
  // failed to report; quarantine it instead of deleting.
  private async clearStaleWorktreeDirectory(absoluteWorktreePath: string): Promise<void> {
    // Nothing at the path means nothing to clear. Falling through would hand a
    // missing directory to the trasher, which fails with ENOENT and turns a
    // recoverable stale registration into a permanent creation failure.
    const dirProbe = await probePathExists(absoluteWorktreePath);
    if (dirProbe === "missing") {
      return;
    }
    if (dirProbe === "unknown") {
      throw new GitOperationError(
        "clear-stale-directory",
        `Cannot verify whether '${absoluteWorktreePath}' still exists; refusing to clear it`,
      );
    }

    const gitProbe = await probePathExists(path.join(absoluteWorktreePath, PATH_CONSTANTS.GIT_DIR));

    if (gitProbe === "unknown") {
      throw new GitOperationError(
        "clear-stale-directory",
        `Cannot verify whether '${absoluteWorktreePath}' is a live checkout; refusing to clear it`,
      );
    }

    if (this.staleDirectoryTrasher) {
      try {
        const trashPath = await this.staleDirectoryTrasher(absoluteWorktreePath);
        this.logger.info(`  - Moved stale directory at '${absoluteWorktreePath}' to trash ('${trashPath}')`);
        return;
      } catch (error) {
        // Cannot preserve it -> refuse to clear it (the caller's worktree
        // creation fails rather than silently deleting unknown content).
        throw new GitOperationError(
          "clear-stale-directory",
          `Cannot move stale directory '${absoluteWorktreePath}' to trash: ${getErrorMessage(error)}`,
          error instanceof Error ? error : undefined,
        );
      }
    }

    if (gitProbe === "exists") {
      const quarantinePath = await quarantineDirectory(absoluteWorktreePath);
      this.logger.warn(
        `  - ⚠️ Directory at '${absoluteWorktreePath}' contains a .git; quarantined to '${quarantinePath}' instead of deleting.`,
      );
      return;
    }

    await fs.rm(absoluteWorktreePath, { recursive: true, force: true });
  }

  async checkWorktreeStatus(worktreePath: string): Promise<boolean> {
    return this.statusService.checkWorktreeStatus(worktreePath);
  }

  async hasUnpushedCommits(worktreePath: string): Promise<boolean> {
    const metadata = await this.metadataService.loadMetadataFromPath(this.bareRepoPath, worktreePath);
    return this.statusService.hasUnpushedCommits(worktreePath, metadata?.lastSyncCommit);
  }

  async hasUpstreamGone(worktreePath: string): Promise<boolean> {
    return this.statusService.hasUpstreamGone(worktreePath);
  }

  async hasStashedChanges(worktreePath: string): Promise<boolean> {
    return this.statusService.hasStashedChanges(worktreePath);
  }

  async getFullWorktreeStatus(worktreePath: string, includeDetails = false): Promise<WorktreeStatusResult> {
    const metadata = await this.metadataService.loadMetadataFromPath(this.bareRepoPath, worktreePath);
    return this.statusService.getFullWorktreeStatus(
      worktreePath,
      includeDetails,
      metadata?.lastSyncCommit,
      metadata?.lastKnownRemoteTip,
    );
  }

  /** Map of remote branch name (without "origin/") → tip oid, from the bare repo. */
  async getRemoteBranchTips(): Promise<Map<string, string>> {
    const git = this.getGit();
    const raw = await git.raw(["for-each-ref", "--format=%(refname:short) %(objectname)", GIT_CONSTANTS.REFS.REMOTES]);
    const tips = new Map<string, string>();
    for (const line of raw.split("\n")) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      const spaceIdx = trimmed.lastIndexOf(" ");
      if (spaceIdx <= 0) continue;
      const ref = trimmed.slice(0, spaceIdx);
      const oid = trimmed.slice(spaceIdx + 1);
      if (!ref.startsWith(GIT_CONSTANTS.REMOTE_PREFIX) || ref === `${GIT_CONSTANTS.REMOTE_PREFIX}HEAD`) continue;
      tips.set(ref.slice(GIT_CONSTANTS.REMOTE_PREFIX.length), oid);
    }
    return tips;
  }

  async recordRemoteTip(worktreePath: string, branchName: string, oid: string): Promise<void> {
    await this.metadataService.recordRemoteTip(
      this.bareRepoPath,
      worktreePath,
      `${GIT_CONSTANTS.REMOTE_PREFIX}${branchName}`,
      oid,
    );
  }

  async hasModifiedSubmodules(worktreePath: string): Promise<boolean> {
    return this.statusService.hasModifiedSubmodules(worktreePath);
  }

  async hasOperationInProgress(worktreePath: string): Promise<boolean> {
    return this.statusService.hasOperationInProgress(worktreePath);
  }

  async getCurrentBranch(): Promise<string> {
    const git = this.getGit();
    const branchSummary = await git.branch();
    return branchSummary.current;
  }

  private async detectDefaultBranch(bareGit: SimpleGit): Promise<string> {
    const originHeadPrefix = "refs/remotes/origin/";
    try {
      // Try to get the symbolic ref for origin/HEAD
      const headRef = await bareGit.raw(["symbolic-ref", "refs/remotes/origin/HEAD"]);
      // Extract branch name from refs/remotes/origin/main or refs/remotes/origin/master
      const ref = headRef.trim();
      const branch = ref.startsWith(originHeadPrefix) ? ref.slice(originHeadPrefix.length) : "";
      if (branch) {
        return branch;
      }
    } catch {
      // If that fails, try to set HEAD automatically
      try {
        await bareGit.raw(["remote", "set-head", "origin", "-a"]);
        const headRef = await bareGit.raw(["symbolic-ref", "refs/remotes/origin/HEAD"]);
        const ref = headRef.trim();
        const branch = ref.startsWith(originHeadPrefix) ? ref.slice(originHeadPrefix.length) : "";
        if (branch) {
          return branch;
        }
      } catch {
        // If all else fails, try to detect from remote branches
        try {
          const remoteBranches = await bareGit.branch(["-r"]);
          const commonDefaults = GIT_CONSTANTS.COMMON_DEFAULT_BRANCHES;
          for (const defaultName of commonDefaults) {
            if (remoteBranches.all.some((branch) => branch === `origin/${defaultName}`)) {
              return defaultName;
            }
          }
        } catch {
          // Ignore and fall through to default
        }
      }
    }
    // Final fallback
    return GIT_CONSTANTS.DEFAULT_BRANCH;
  }

  setLfsSkipEnabled(value: boolean): void {
    this.lfsSkipOverride = value;
  }

  private isLfsSkipEnabled(): boolean {
    return this.config.skipLfs || this.lfsSkipOverride;
  }

  async getWorktrees(): Promise<{ path: string; branch: string }[]> {
    const bareGit = this.getCachedGit(this.bareRepoPath);
    return this.getWorktreesFromBare(bareGit);
  }

  async isWorktreeBehind(worktreePath: string): Promise<boolean> {
    const worktreeGit = this.getCachedGit(worktreePath);
    try {
      // Get the current branch
      const branchSummary = await worktreeGit.branch();
      const currentBranch = branchSummary.current;

      // Check if the branch has an upstream
      const upstreamInfo = await worktreeGit.raw(["rev-parse", "--abbrev-ref", `${currentBranch}@{upstream}`]);
      if (!upstreamInfo.trim()) {
        return false; // No upstream, can't be behind
      }

      // Count commits behind upstream
      const behindCount = await worktreeGit.raw(["rev-list", "--count", `HEAD..${upstreamInfo.trim()}`]);
      return parseInt(behindCount.trim(), 10) > 0;
    } catch {
      // If any command fails, assume not behind
      return false;
    }
  }

  async updateWorktree(worktreePath: string): Promise<void> {
    const worktreeGit = this.getCachedGit(worktreePath, this.isLfsSkipEnabled());

    // Perform a fast-forward merge
    const branchSummary = await worktreeGit.branch();
    const currentBranch = branchSummary.current;

    await worktreeGit.merge([`origin/${currentBranch}`, "--ff-only"]);

    // Update metadata after successful update (use path-based method)
    try {
      const currentCommit = await worktreeGit.revparse(["HEAD"]);
      await this.metadataService.updateLastSyncFromPath(
        this.bareRepoPath,
        worktreePath,
        currentCommit.trim(),
        "updated",
        this.defaultBranch,
      );
    } catch (metadataError) {
      this.logger.warn(`Failed to update metadata for worktree: ${metadataError}`);
    }
  }

  async hasDivergedHistory(worktreePath: string, expectedBranch: string): Promise<boolean> {
    const worktreeGit = this.getCachedGit(worktreePath);

    // Validate branch matches
    const branchInfo = await worktreeGit.branch();
    if (branchInfo.current !== expectedBranch) {
      this.logger.warn(`Branch mismatch in hasDivergedHistory: expected ${expectedBranch}, got ${branchInfo.current}`);
      return false; // Conservative: assume can fast-forward
    }

    try {
      // Check if HEAD is an ancestor of the remote branch (can fast-forward)
      await worktreeGit.raw(["merge-base", "--is-ancestor", "HEAD", `origin/${expectedBranch}`]);
      return false; // Can fast-forward
    } catch {
      return true; // Histories have diverged
    }
  }

  async canFastForward(worktreePath: string, branch: string): Promise<boolean> {
    const worktreeGit = this.getCachedGit(worktreePath);
    try {
      // Get the merge base between HEAD and the remote branch
      const mergeBase = await worktreeGit.raw(["merge-base", "HEAD", `origin/${branch}`]);
      const mergeBaseSha = mergeBase.trim();

      // Get current HEAD SHA
      const headSha = await worktreeGit.revparse(["HEAD"]);
      const headShaTrimmed = headSha.trim();

      // If merge base equals HEAD, then HEAD is an ancestor of remote and can fast-forward
      return mergeBaseSha === headShaTrimmed;
    } catch {
      // If merge-base fails, branches have diverged
      return false;
    }
  }

  async isLocalAheadOfRemote(worktreePath: string, branch: string): Promise<boolean> {
    const worktreeGit = this.getCachedGit(worktreePath);
    try {
      // Get the merge base between HEAD and the remote branch
      const mergeBase = await worktreeGit.raw(["merge-base", "HEAD", `origin/${branch}`]);
      const mergeBaseSha = mergeBase.trim();

      // Get remote branch SHA
      const remoteSha = await worktreeGit.revparse([`origin/${branch}`]);
      const remoteShaTrimmed = remoteSha.trim();

      // If merge base equals remote, local is ahead (remote is ancestor of local)
      return mergeBaseSha === remoteShaTrimmed;
    } catch {
      return false;
    }
  }

  async classifyRemoteRelationship(worktreePath: string, branch: string): Promise<RemoteRelationship> {
    const worktreeGit = this.getCachedGit(worktreePath);

    let headSha: string;
    let remoteSha: string;
    try {
      headSha = (await worktreeGit.revparse(["HEAD"])).trim();
      remoteSha = (await worktreeGit.revparse([`refs/remotes/origin/${branch}`])).trim();
    } catch {
      return "diverged";
    }

    if (headSha === remoteSha) return "up_to_date";

    let mergeBase = "";
    let mergeBaseFailed = false;
    try {
      mergeBase = (await worktreeGit.raw(["merge-base", "HEAD", `origin/${branch}`])).trim();
    } catch {
      mergeBaseFailed = true;
    }
    // simple-git swallows merge-base exit 1 and returns "" — treat empty output as failure too.
    if (mergeBaseFailed || !mergeBase) {
      if (await this.isShallowRepository(worktreeGit)) return "indeterminate_shallow";
      return "diverged";
    }
    if (mergeBase === headSha) return "fast_forward";
    if (mergeBase === remoteSha) return "local_ahead";
    return "diverged";
  }

  private async isShallowRepository(git: SimpleGit): Promise<boolean> {
    try {
      const output = await git.raw(["rev-parse", "--is-shallow-repository"]);
      return output.trim() === "true";
    } catch {
      return false;
    }
  }

  async getChangedPathsInRange(worktreePath: string, fromRef: string, toRef: string): Promise<string[] | null> {
    const worktreeGit = this.getCachedGit(worktreePath);
    try {
      const out = await worktreeGit.raw([
        "-c",
        "core.quotePath=false",
        "diff",
        "--name-only",
        "--no-renames",
        `${fromRef}..${toRef}`,
      ]);
      // Don't .trim() entries — leading/trailing whitespace is valid in POSIX
      // paths and `core.quotePath=false` only affects high-byte quoting, not
      // whitespace. Strip a trailing CR for Windows line endings only.
      return out
        .split("\n")
        .map((l) => l.replace(/\r$/, ""))
        .filter((l) => l.length > 0);
    } catch (error) {
      this.logger.warn(`Failed to compute diff ${fromRef}..${toRef} in ${worktreePath}: ${getErrorMessage(error)}`);
      return null;
    }
  }

  async compareTreeContent(worktreePath: string, branch: string): Promise<boolean> {
    const worktreeGit = this.getCachedGit(worktreePath);
    try {
      // Get the tree SHA for the current HEAD
      const localTree = await worktreeGit.raw(["rev-parse", "HEAD^{tree}"]);
      // Get the tree SHA for the remote branch
      const remoteTree = await worktreeGit.raw(["rev-parse", `origin/${branch}^{tree}`]);

      return localTree.trim() === remoteTree.trim();
    } catch (error) {
      this.logger.error(`Error comparing tree content: ${error}`);
      return false; // Assume trees are different if we can't compare
    }
  }

  // Would checking out `origin/<branch>` write over something git is currently
  // ignoring? Comparing every ignored path against every upstream path is
  // O(ignored x upstream) and runs synchronously with the repo lock held, which
  // is minutes of a blocked event loop on a monorepo. Index the upstream tree
  // once instead, then answer each ignored path in constant time per segment.
  private wouldOverwriteIgnoredPaths(ignoredRaw: string, upstreamRaw: string): boolean {
    const upstreamFiles = new Set<string>();
    // Files plus every ancestor directory: membership then means "upstream has
    // content at this path or somewhere beneath it".
    const upstreamPaths = new Set<string>();
    for (const upstreamPath of upstreamRaw.split("\0")) {
      if (!upstreamPath) continue;
      upstreamFiles.add(upstreamPath);
      upstreamPaths.add(upstreamPath);
      for (let slash = upstreamPath.indexOf("/"); slash !== -1; slash = upstreamPath.indexOf("/", slash + 1)) {
        upstreamPaths.add(upstreamPath.slice(0, slash));
      }
    }

    for (const entry of ignoredRaw.split("\0")) {
      if (!entry) continue;
      // `--directory` reports collapsed directories with a trailing slash.
      const localPath = entry.endsWith("/") ? entry.slice(0, -1) : entry;
      if (upstreamPaths.has(localPath)) return true;
      // The reverse shadowing case: upstream has a file (or submodule) exactly
      // where this ignored path sits inside a directory.
      for (let slash = localPath.indexOf("/"); slash !== -1; slash = localPath.indexOf("/", slash + 1)) {
        if (upstreamFiles.has(localPath.slice(0, slash))) return true;
      }
    }
    return false;
  }

  async resetToUpstream(worktreePath: string, branch: string, expectedHead?: string): Promise<boolean> {
    const worktreeGit = this.getCachedGit(worktreePath, this.isLfsSkipEnabled());
    const status = await worktreeGit.status(["--ignore-submodules=none"]);
    if (!status.isClean()) return false;

    // `--directory` collapses a wholly-ignored directory to one entry, so a
    // node_modules with 150k files costs one path instead of 150k. Directories
    // that also hold tracked files are still expanded, so nothing is missed.
    const [ignoredRaw, upstreamRaw] = await Promise.all([
      worktreeGit.raw([
        "ls-files",
        "-z",
        "--others",
        "--ignored",
        "--exclude-standard",
        "--directory",
        "--no-empty-directory",
      ]),
      worktreeGit.raw(["ls-tree", "-rz", "--name-only", `origin/${branch}`]),
    ]);
    if (this.wouldOverwriteIgnoredPaths(ignoredRaw, upstreamRaw)) return false;

    if (expectedHead) {
      const currentHead = (await worktreeGit.revparse(["HEAD"])).trim();
      if (currentHead !== expectedHead) return false;
    }

    try {
      await worktreeGit.raw(["checkout", "-B", branch, `origin/${branch}`, "--no-overwrite-ignore"]);
    } catch (error) {
      if (/would be overwritten by checkout/i.test(getErrorMessage(error))) return false;
      throw error;
    }

    // Update metadata after reset (use path-based method)
    try {
      const currentCommit = await worktreeGit.revparse(["HEAD"]);
      await this.metadataService.updateLastSyncFromPath(
        this.bareRepoPath,
        worktreePath,
        currentCommit.trim(),
        "updated",
        this.defaultBranch,
      );
    } catch (metadataError) {
      this.logger.warn(`Failed to update metadata after reset: ${metadataError}`);
    }
    return true;
  }

  async getCurrentCommit(worktreePath: string): Promise<string> {
    const worktreeGit = this.getCachedGit(worktreePath);
    const commit = await worktreeGit.revparse(["HEAD"]);
    return commit.trim();
  }

  async getRemoteCommit(ref: string): Promise<string> {
    // Use the bare repository to read remote commit to avoid dependency on main worktree path
    const git = this.getCachedGit(this.bareRepoPath);
    const commit = await git.revparse([ref]);
    return commit.trim();
  }

  async branchExists(branchName: string): Promise<{ local: boolean; remote: boolean }> {
    const bareGit = this.getCachedGit(this.bareRepoPath);
    const checkRef = async (ref: string): Promise<boolean> => {
      try {
        // simple-git resolves `show-ref --quiet` when Git exits 1, so keep stdout enabled.
        await bareGit.raw(["show-ref", "--verify", ref]);
        return true;
      } catch {
        return false;
      }
    };

    const [local, remote] = await Promise.all([
      checkRef(`${GIT_CONSTANTS.REFS.HEADS}${branchName}`),
      checkRef(`${GIT_CONSTANTS.REFS.REMOTES}/${branchName}`),
    ]);

    return { local, remote };
  }

  async getLocalBranches(): Promise<string[]> {
    const bareGit = this.getCachedGit(this.bareRepoPath);
    const branches = await bareGit.branch();
    return branches.all;
  }

  private async resolveCreateBranchBaseRef(bareGit: SimpleGit, baseBranch: string): Promise<string> {
    const candidates =
      baseBranch.startsWith(GIT_CONSTANTS.REMOTE_PREFIX) || baseBranch.startsWith("refs/")
        ? [baseBranch]
        : [`${GIT_CONSTANTS.REMOTE_PREFIX}${baseBranch}`, baseBranch];

    for (const candidate of candidates) {
      try {
        await bareGit.revparse(["--verify", candidate]);
        return candidate;
      } catch {
        // Try the next candidate before letting git branch report the original failure.
      }
    }

    return candidates[0];
  }

  async createBranch(branchName: string, baseBranch: string): Promise<void> {
    const bareGit = this.getCachedGit(this.bareRepoPath);
    const baseRef = await this.resolveCreateBranchBaseRef(bareGit, baseBranch);

    await bareGit.raw(["branch", "--no-track", branchName, baseRef]);
    this.logger.info(`Created branch '${branchName}' from '${baseRef}'`);
  }

  async pushBranch(branchName: string): Promise<void> {
    const bareGit = this.getCachedGit(this.bareRepoPath);

    await bareGit.push(["origin", `${branchName}:${branchName}`, "-u"]);
    this.logger.info(`Pushed branch '${branchName}' to remote`);
  }

  async getWorktreeMetadata(worktreePath: string): Promise<SyncMetadata | null> {
    return this.metadataService.loadMetadataFromPath(this.bareRepoPath, worktreePath);
  }

  private async getWorktreesFromBare(
    bareGit: SimpleGit,
    includeDetached = false,
  ): Promise<{ path: string; branch: string; isPrunable?: boolean }[]> {
    const result = await bareGit.raw(["worktree", "list", "--porcelain"]);
    return parseWorktreeListPorcelain(result)
      .filter((w) => includeDetached || (!w.detached && w.branch !== null))
      .map((w) => ({
        path: w.path,
        branch: w.branch ?? "",
        isPrunable: w.prunable,
      }));
  }
}
